import { BookCopy } from "lucide-react";
import { SearchSource } from "../types/chat";
import { useTranslation } from "react-i18next";
import { useTheme } from "components/core/theme-provider";
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "components/ui/dialog";

interface SearchSourcesProps {
    sources: SearchSource[];
}

export const SearchSources = ({ sources }: SearchSourcesProps) => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const [showModal, setShowModal] = useState(false);

    const cardClass = theme === 'dark' ? "bg-white-25 hover:bg-primary-600 transition duration-200 rounded-lg p-3 flex flex-col space-y-2 font-medium shado" : "bg-gray-100 hover:bg-gray-200 transition duration-200 rounded-lg p-3 flex flex-col space-y-2 font-medium";


    const renderSource = (src: SearchSource, idx: number) => {
        return (
            <a
                key={idx}
                href={src.metadata.url}
                target="_blank"
                rel="noreferrer"
                className={cardClass}
            >
                <p className="text-xs overflow-hidden whitespace-nowrap text-ellipsis">
                    {src.metadata.title}
                </p>
                <div className="flex flex-row items-center justify-between w-full">
                    <div className="flex flex-row items-center space-x-1">
                        <img
                            width="16"
                            height="16"
                            alt="favicon"
                            className="rounded-lg h-4 w-4"
                            src={`https://s2.googleusercontent.com/s2/favicons?domain_url=${src.metadata.url}`}
                        />
                        <p className="text-xs overflow-hidden whitespace-nowrap truncate sm:max-w-16 lg:max-w-20">
                            {src.metadata.url.split('://')[1].split('.')[0]}
                        </p>
                    </div>
                    <div className="flex flex-row items-center space-x-1 text-xs">
                        <div className="bg-gray-400 h-[4px] w-[4px] rounded-full"></div>
                        <span>{idx + 1}</span>
                    </div>
                </div>
            </a>
        )
    };

    return (
        <div className="flex flex-col space-y-2 mb-4">
            {/* Header */}
            <div className="flex flex-row items-center space-x-2">
                <BookCopy className="w-4 h-4" />
                <h3 className="font-medium text-lg">{t('SOURCES')}</h3>
            </div>

            {/* Sources Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {sources.slice(0, 3).map((src, idx) => renderSource(src, idx))}

                {sources.length > 3 && (
                    <button onClick={() => setShowModal(true)} className={cardClass}>
                        <div className="flex flex-row items-center space-x-1">
                            {sources.slice(3, 6).map((src, idx) => (
                                <img
                                    key={idx}
                                    width="16"
                                    height="16"
                                    alt="favicon"
                                    className="rounded-lg h-4 w-4"
                                    src={`https://s2.googleusercontent.com/s2/favicons?domain_url=${src.metadata.url}`}
                                />
                            ))}
                        </div>
                        <p className="text-xs text-gray-500">{t("VIEW")} {" "} {sources.length - 3} {" "} {t("MORE")}</p>
                    </button>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="relative z-50">
                    <Dialog open={showModal} onOpenChange={setShowModal}>
                        <DialogContent className="w-full max-w-md transform rounded-2xl border border-light-200 dark:border-dark-200 p-6 text-left align-middle shadow-xl transition-all">

                            <DialogTitle className="text-lg font-medium leading-6 dark:text-white">
                                {t('SOURCES')}
                            </DialogTitle>

                            <div className="grid grid-cols-2 gap-2 overflow-auto max-h-[300px] mt-2 pr-2">
                                {sources.map((src, idx) => renderSource(src, idx))}
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>)}
        </div>
    );
}